import { format } from "date-fns";

import {
  EMPTY_NEW_PATIENT_SEED,
  NewPatientSeed,
  toPhoneDigits
} from "components/patient-search/new-patient-seed.utils";
import type { CallContact, PracticeCallPatient } from "services/dialstack";

import { displayNumber } from "../call-display.util";
import {
  isColleagueLine,
  isInternalExtension,
  webrtcDeviceUserId
} from "../phone-match.util";

// Who a call is with, in precedence order: a saved contact, a matched patient,
// the carrier's caller-ID name (CNAM), or just the number. Every surface that
// names a caller (softphone card, call log, workspace, drawer, voicemails) goes
// through here so the precedence and the "Caller ID" labelling stay identical.
// A call carries both a contact and a patient only after staff attached a
// patient to a contact's call (ENG-2563): the contact is who was on the line,
// the patient is who the call was about, so the contact names the caller.
export type CallerIdentity = {
  patient?: PracticeCallPatient | null;
  contact?: CallContact | null;
  fromName?: string | null;
  number?: string | null;
};

export const UNKNOWN_CALLER = "Unknown caller";

// The patient-phone matches: a patient the number itself named. Taking them
// off one call is not the fix — the number on the patient's record is — so
// Remove from patient is offered only for a manual attach (and for the
// click-to-call rows it carried forward, which have no match at all).
const PATIENT_PHONE_MATCHES = [
  "phone",
  "secondary_phone",
  "responsible_party_phone"
];

export function isPatientRemovable(matchedOn: string | null | undefined) {
  return !matchedOn || !PATIENT_PHONE_MATCHES.includes(matchedOn);
}

// Who Call back dials as (the `CallParty` of `useMakeCall`). A patient the
// number itself named goes along, so the outbound call files under them; a
// patient attached by hand does not — that is how a vendor's later calls
// ended up under a patient (ENG-2563). The backend names the new call from
// the number either way.
export function callBackParty(
  patient: PracticeCallPatient | null,
  contact: CallContact | null | undefined,
  matchedOn: string | null | undefined
): PracticeCallPatient | { contact: CallContact } | undefined {
  const contactParty = contact ? { contact } : undefined;
  if (matchedOn === "manual") return contactParty;
  return patient ?? contactParty;
}

// "Attached by Sam Reed · Oct 20, 2026" for a call staff filed by hand.
export function attachedByLabel(
  attachedBy: { first_name: string; last_name: string } | null | undefined,
  attachedAt: string | null | undefined
): string | null {
  const name = attachedBy
    ? `${attachedBy.first_name} ${attachedBy.last_name}`.trim()
    : "";
  if (!name) return null;
  const when = attachedAt
    ? ` · ${format(new Date(attachedAt), "MMM d, yyyy")}`
    : "";
  return `Attached by ${name}${when}`;
}

export function patientDisplayName(patient: PracticeCallPatient) {
  return `${patient.preferred_name || patient.first_name} ${patient.last_name}`.trim();
}

export function callerDisplayName({
  patient,
  contact,
  fromName,
  number
}: CallerIdentity): string {
  if (contact?.name) return contact.name;
  if (patient) return patientDisplayName(patient);
  // The phone system can echo a web softphone's device id back as the name.
  const callerId = fromName?.trim();
  if (callerId && webrtcDeviceUserId(callerId) === null) return callerId;
  if (isInternalExtension(number)) return `Ext ${number?.trim()}`;
  // A leg from a colleague's web softphone carries their device id, which is
  // no name for a person (ENG-2061).
  if (isColleagueLine(number)) return "Colleague";
  if (number) return displayNumber(number);
  return UNKNOWN_CALLER;
}

export type CallerSubtitle = {
  kind: "patient" | "contact" | "internal" | "callerId" | "unknown";
  text: string;
};

// The line under the name. A CNAM name is always labelled so staff never
// mistake the carrier's guess for a verified identity.
export function callerSubtitle({
  patient,
  contact,
  fromName,
  number
}: CallerIdentity): CallerSubtitle {
  const formatted = number ? displayNumber(number) : "";
  if (contact) {
    return {
      kind: "contact",
      text: [
        formatted,
        contact.organization || "Contact",
        // Attached to a patient by hand: say who the call was about.
        patient ? `About ${patientDisplayName(patient)}` : null
      ]
        .filter(Boolean)
        .join(" · ")
    };
  }
  if (patient) {
    return {
      kind: "patient",
      text: [formatted, "Patient"].filter(Boolean).join(" · ")
    };
  }
  // A colleague's line: there is no patient to match, and the name is the
  // phone system's own, not a carrier's guess. Only an extension is worth
  // showing beside the name; a web softphone's device id is not.
  if (isColleagueLine(number)) {
    return {
      kind: "internal",
      text:
        fromName?.trim() && isInternalExtension(number)
          ? `Ext ${number?.trim()} · Internal call`
          : "Internal call"
    };
  }
  if (fromName?.trim()) {
    return {
      kind: "callerId",
      text: [formatted, "Caller ID · not matched"].filter(Boolean).join(" · ")
    };
  }
  return { kind: "unknown", text: "Not matched to a patient" };
}

export function relationshipLabel(relationship?: string | null): string {
  return relationship?.trim() ?? "";
}

// Transcript / phone-note speaker label for the remote party, e.g.
// "Jane Smith — caretaker".
export function remoteSpeakerLabel(
  identity: CallerIdentity,
  relationship?: string | null,
  fallback = "Caller"
): string {
  const name =
    identity.patient || identity.contact || identity.fromName?.trim()
      ? callerDisplayName(identity)
      : fallback;
  const label = relationshipLabel(relationship);
  return label ? `${name} — ${label}` : name;
}

const titleCase = (word: string) =>
  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

// CNAM strings are all-caps and arrive in either "FIRST LAST" or "LAST, FIRST"
// order. Only the comma form is unambiguous, so anything else puts the first
// token in first_name and the rest in last_name — never guess "LAST FIRST".
// Everything is editable in the form; this just saves typing.
export function seedFromCallerId(
  fromName: string | null | undefined,
  number?: string | null
): NewPatientSeed {
  const seed: NewPatientSeed = {
    ...EMPTY_NEW_PATIENT_SEED,
    phone: number ? toPhoneDigits(number) : ""
  };
  const raw = fromName?.trim() ?? "";
  if (!raw) return seed;
  if (raw.includes(",")) {
    const [last = "", first = ""] = raw.split(",").map((part) => part.trim());
    const [firstName = "", ...middle] = first.split(/\s+/).filter(Boolean);
    return {
      ...seed,
      first_name: titleCase(firstName),
      middle_name: middle.map(titleCase).join(" "),
      last_name: last.split(/\s+/).filter(Boolean).map(titleCase).join(" ")
    };
  }
  const [firstName = "", ...rest] = raw.split(/\s+/).filter(Boolean);
  return {
    ...seed,
    first_name: titleCase(firstName),
    last_name: rest.map(titleCase).join(" ")
  };
}
